import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import {
  Facebook,
  Mail,
  Phone,
  MapPin,
  ChevronRight,
  ArrowUp,
} from "lucide-react";
import OptimizedImage from "../../../utils/landing/OptimizedImage";
import { useSmoothNavigate } from "../../../utils/landing/useSmoothNavigate";

function TikTokIcon({ className }) {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="currentColor"
      className={className}
      aria-hidden="true"
    >
      <path d="M16.6 5.82c-.9-.98-1.4-2.26-1.4-3.62h-3.15v13.86c0 1.53-1.24 2.77-2.77 2.77a2.77 2.77 0 0 1-2.77-2.77 2.77 2.77 0 0 1 2.77-2.77c.29 0 .56.04.82.12v-3.19c-.27-.03-.54-.05-.82-.05-3.29 0-5.96 2.67-5.96 5.96s2.67 5.96 5.96 5.96 5.96-2.67 5.96-5.96V9.4a8.35 8.35 0 0 0 4.87 1.55V7.81c-1.28 0-2.44-.41-3.4-1.11a5.94 5.94 0 0 1-.11-.88z" />
    </svg>
  );
}

export function Footer() {
  const [showScrollTop, setShowScrollTop] = useState(false);
  const smoothNavigate = useSmoothNavigate();

  // Mostrar botón de scroll al top cuando bajemos lo suficiente
  useEffect(() => {
    const handleScroll = () => {
      if (typeof window !== "undefined") {
        setShowScrollTop(window.scrollY > 300);
      }
    };
    if (typeof window !== "undefined") {
      window.addEventListener("scroll", handleScroll);
      return () => window.removeEventListener("scroll", handleScroll);
    }
  }, []);

  const scrollToTop = () => {
    if (typeof window !== "undefined") {
      window.scrollTo({
        top: 0,
        behavior: "smooth",
      });
    }
  };

  return (
    <footer className="relative bg-primary-custom text-secondary-custom">
      {/* Forma decorativa en la parte superior */}
      <div className="absolute top-0 left-0 right-0 h-8 overflow-hidden">
        <div className="w-full h-16 bg-white rounded-b-[100%] opacity-10"></div>
      </div>

      <div className="max-w-7xl mx-auto px-6 pt-20 pb-10">
        {/* Contenido principal */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-y-12 gap-x-8">
          {/* Columna 1: Información de la institución */}
          <div className="space-y-6">
            <div className="flex items-center gap-4 group">
              <div className="p-1 bg-white rounded-lg shadow-lg transition-transform duration-300 group-hover:scale-105">
                <OptimizedImage
                  src="/img/landing/logo.webp"
                  alt="Logo"
                  width={65}
                  height={65}
                  className="rounded-md"
                />
              </div>
              <div className="flex flex-col">
                <span className="text-xl font-bold text-white tracking-wide">
                  I.E.P. SAN ISIDRO
                </span>
                <span className="text-xs text-white/70">
                  Excelencia educativa
                </span>
              </div>
            </div>

            <p className="text-white/80 text-sm text-justify leading-relaxed">
              Formando líderes para triunfar desde 1998. Comprometidos con una
              educación de calidad y valores firmes para las futuras
              generaciones.
            </p>

            <div className="flex space-x-4 pt-2">
              <a
                href="https://www.facebook.com/IEPSanIsidro"
                target="_blank"
                rel="noreferrer"
                className="bg-white/10 hover:bg-white/20 p-2 rounded-full transition-all transform hover:scale-110"
                aria-label="Facebook"
              >
                <Facebook className="h-5 w-5 text-white" />
              </a>
              <a
                href="https://www.tiktok.com/@i.e.p_sanisidro01?_r=1&_t=ZS-994fu1vN9NP"
                target="_blank"
                rel="noreferrer"
                className="bg-white/10 hover:bg-white/20 p-2 rounded-full transition-all transform hover:scale-110"
                aria-label="TikTok"
              >
                <TikTokIcon className="h-5 w-5 text-white" />
              </a>
            </div>
          </div>

          {/* Columna 2: Contacto */}
          <div className="space-y-6">
            <h3 className="text-lg font-semibold text-white relative inline-block">
              Contacto
              <span className="absolute -bottom-2 left-0 w-12 h-1 bg-yellow-400 rounded-full"></span>
            </h3>

            <div className="space-y-4">
              <a
                href="https://maps.google.com"
                target="_blank"
                rel="noreferrer"
                className="flex items-start gap-3 group"
              >
                <MapPin className="h-5 w-5 text-yellow-400 mt-0.5 flex-shrink-0" />
                <span className="text-sm text-white/90 group-hover:text-white transition-colors">
                  Calle Independencia 765, Zaña
                </span>
              </a>

              <a
                href="mailto:informes@iepsanisidro.edu.pe"
                className="flex items-center gap-3 group"
              >
                <Mail className="h-5 w-5 text-yellow-400 flex-shrink-0" />
                <span className="text-sm text-white/90 group-hover:text-white transition-colors">
                  informes@iepsanisidro.edu.pe
                </span>
              </a>

              <a
                href="tel:+51741234567"
                className="flex items-center gap-3 group"
              >
                <Phone className="h-5 w-5 text-yellow-400 flex-shrink-0" />
                <span className="text-sm text-white/90 group-hover:text-white transition-colors">
                  +51 941 699 392
                </span>
              </a>
            </div>
          </div>

          {/* Columna 3: Enlaces rápidos */}
          <div className="space-y-6">
            <h3 className="text-lg font-semibold text-white relative inline-block">
              Enlaces rápidos
              <span className="absolute -bottom-2 left-0 w-12 h-1 bg-yellow-400 rounded-full"></span>
            </h3>

            <nav className="flex flex-col space-y-3">
              {[
                { name: "Inicio", href: "/#inicio" },
                { name: "Sobre Nosotros", href: "/#sobre-nosotros" },
                { name: "Niveles", href: "/#niveles" },
                { name: "Metodología", href: "/#metodologia" },
                { name: "Eventos", href: "/#eventos" },
              ].map((link) => (
                <a
                  key={link.name}
                  href={link.href}
                  onClick={(e) => smoothNavigate(e, link.href)}
                  className="text-sm text-white/80 hover:text-white transition-colors flex items-center group"
                >
                  <ChevronRight className="h-3 w-3 mr-2 text-yellow-400 opacity-0 group-hover:opacity-100 transform translate-x-0 group-hover:translate-x-1 transition-all duration-300" />
                  <span>{link.name}</span>
                </a>
              ))}
            </nav>
          </div>

          {/* Columna 4: Horarios */}
          <div className="space-y-6">
            <h3 className="text-lg font-semibold text-white relative inline-block">
              Horario de atención
              <span className="absolute -bottom-2 left-0 w-12 h-1 bg-yellow-400 rounded-full"></span>
            </h3>

            <div className="space-y-3 text-sm">
              <div className="flex justify-between items-center">
                <span className="text-white/90">Lunes - Viernes:</span>
                <span className="text-white font-medium">7:30 a.m. - 01:00 p.m.</span>
              </div>
              <div className="pt-3">
                <div className="p-4 rounded-lg bg-white/5 border border-white/10">
                  <p className="text-white text-sm">
                    ¿Tienes alguna consulta? Estamos para ayudarte.
                    <a
                      href="#contacto"
                      onClick={(e) => smoothNavigate(e, "#contacto")}
                      className="block mt-2 text-yellow-400 hover:underline hover:text-yellow-400 font-medium"
                    >
                      Contáctanos aquí →
                    </a>
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Línea divisoria */}
        <div className="border-t border-white/10 my-10"></div>

        {/* Copyright */}
        <div className="flex flex-col md:flex-row justify-between items-center text-white/60 text-sm">
          <p>
            &copy; {new Date().getFullYear()} I.E.P. San Isidro. Todos los
            derechos reservados.
          </p>
          <div className="flex mt-4 md:mt-0 space-x-6">
            <Link
              to="/privacy-policy"
              className="hover:text-white/90 transition-colors"
            >
              Política de Privacidad
            </Link>
            <Link
              to="/terms-of-service"
              className="hover:text-white/90 transition-colors"
            >
              Términos de Servicio
            </Link>
          </div>
        </div>
      </div>

      {/* Botón para ir arriba */}
      {showScrollTop && (
        <button
          onClick={scrollToTop}
          className="fixed bottom-6 right-6 bg-primary-custom text-white p-3 rounded-full shadow-lg hover:bg-yellow-700 transition-all duration-300 hover:scale-110 z-50"
          aria-label="Volver arriba"
        >
          <ArrowUp className="h-5 w-5" />
        </button>
      )}
    </footer>
  );
}
